// src/pages/AboutMePage.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, type Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import Container from '../components/common/Container';
import Card from '../components/common/Card';
import BackToTopButton from '../components/common/BackToTopButton';
import bulldozerImage from '../assets/images/Bulldozer-Tap-01.webp';
import landingBackgroundImg from '../assets/images/Landing.webp';
import { blogPosts } from '../data/blogPosts';

// Animation variants
const animations: { fadeIn: Variants; staggerContainer: Variants } = {
  fadeIn: {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  },
  staggerContainer: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }
};

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url(${landingBackgroundImg}) no-repeat center center;
    background-size: cover;
    z-index: -2;
  }

  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: -1;
  }
`;

const AboutMeSection = styled.section`
  width: 100%;
  padding: 8rem 0;
  position: relative;
  background: rgba(0, 0, 0, 0.7);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PageHeading = styled.h1`
  text-align: center;
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const BioSection = styled.section`
  width: 100%;
  padding: 8rem 0;
  position: relative;
  background: rgba(0, 0, 0, 0.5);
  min-height: 100vh;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    min-height: auto;
    padding: 6rem 0;
  }
`;

const BioBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${bulldozerImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 2;
  }

  @media (max-width: 768px) {
    background-attachment: scroll;
  }
`;

const BioContent = styled.div`
  position: relative;
  z-index: 3;
  max-width: 800px;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 1rem;
  text-align: center;
  margin: 0 auto;
  padding: 4rem 2rem;
  width: 90%;

  @media (max-width: 768px) {
    padding: 2rem;
    width: 95%;
  }
`;

const BioText = styled.p`
  font-size: 1.25rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.5;
  }
`;

interface Skill {
  name: string;
  proficiency: number;
  description: string;
  icon?: string;
}

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const SkillHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 1rem;
`;

const SkillTitle = styled.h3`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  text-align: center;
  width: 100%;
`;

const SkillLevel = styled.div`
  margin: 1.5rem 0;
  width: 100%;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 12px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  overflow: hidden;
  margin-top: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
`;

const ProgressBar = styled.div<{ width: number }>`
  width: ${props => props.width}%;
  height: 100%;
  background: ${props => props.theme.colors.primary};
  border-radius: 6px;
  transition: width 1s ease-in-out;
  box-shadow: 0 0 10px rgba(149, 117, 205, 0.3);
`;

const PercentageText = styled.span`
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  display: block;
  text-align: right;
`;

const SkillDescription = styled.p`
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
  line-height: 1.6;
  flex-grow: 1;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const ProjectsSection = styled.section`
  width: 100%;
  padding: 8rem 0;
  position: relative;
  background: rgba(0, 0, 0, 0.5);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  backdrop-filter: blur(5px);
  padding: 2rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ProjectTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  font-size: 1.5rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const ProjectDescription = styled.p`
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.primary};
  line-height: 1.6;
  font-size: 1.1rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const ProjectRole = styled.p`
  font-style: italic;
  color: ${props => props.theme.colors.primary};
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const SectionHeading = styled.h2`
  text-align: center;
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const EnhancedSkillCard = styled(Card)`
  padding: 2rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  backdrop-filter: blur(5px);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const BlogSection = styled.section`
  width: 100%;
  padding: 8rem 0;
  position: relative;
  background: rgba(0, 0, 0, 0.7);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  backdrop-filter: blur(5px);
  padding: 2rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const BlogTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  margin-bottom: 1rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const BlogPreview = styled.p`
  color: white;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  font-size: 1.1rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const BlogMeta = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const BlogDate = styled.span``;

const BlogReadTime = styled.span``;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Tag = styled.span`
  background-color: rgba(255, 255, 255, 0.1);
  color: ${props => props.theme.colors.primary};
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  border: 1px solid ${props => props.theme.colors.primary};
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const FixedNavigation = styled.nav`
  position: fixed;
  top: 1rem;
  right: 2rem;
  display: flex;
  gap: 2rem;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  backdrop-filter: blur(5px);
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  position: relative;
  padding: 0.5rem 0;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const ScrollToTopLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <NavLink to={to} onClick={handleClick}>
      {children}
    </NavLink>
  );
};

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1800px;
  padding: 0 2rem;
`;

const AboutMePage: React.FC = () => {
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScroll = () => {
    setShowScrollButton(window.scrollY > 300);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const skills: Skill[] = [
    {
      name: "Programming",
      proficiency: 90,
      description: "C++, Java, Python, HTML/CSS, Bluebeam, Excel, git. Educational background and creating personal projects and applications."
    },
    {
      name: "Development",
      proficiency: 85,
      description: "C, Assembly, JavaScript, TypeScript, Matplotlib, and more. Educational background and development experience."
    },
    {
      name: 'Artificial Intelligence literacy and experience',
      proficiency: 80,
      description: 'Machine & RL learning, AI agents & frameworks, RAG architectures, MCP protocols, LangChain, PyTorch, Midjourney, SORA, Claude, Grok, ChatGPT, Gemini, and more..'
    },
    {
      name: 'Field Expertise',
      proficiency: 90,
      description: 'Construction scheduling, coordinating, and managing subcontractors to ensure on-time completion of the project.'
    },
    {
      name: 'Project Management',
      proficiency: 80,
      description: 'Scopes, budgeting, contracts, Change Orders, RFIs, submittals, punch lists, and building turnover.'
    },
    {
      name: 'Markup, Report, and Status Generation',
      proficiency: 95,
      description: 'Generating markups and reports, create visual status updates, and track material/equipment deliveries.'
    },
    {
      name: 'ADA, FHA, TAS, ANSI Compliance',
      proficiency: 75,
      description: 'Ensuring compliance with ADA, FHA, TAS, and ANSI standards.'
    },
    {
      name: 'Experience with Local, State, and Federal Regulations & Codes, including OSHA',
      proficiency: 75,
      description: 'Understanding and adhering to local, state, and federal regulations and codes, including OSHA.'
    },
    {
      name: 'City & 3rd-Party Inspections and Materials Testing',
      proficiency: 90,
      description: 'Inspecting and testing materials and systems to ensure compliance with specifications and standards.'
    },
  ];

  const projects = [
    {
      title: 'Multifamily Development',
      description: '388-unit luxury apartment community with modern amenities in Fort Worth, TX.\n',
      role: 'Initial field engineering role focused on on-site coordination.'
    },
    {
      title: 'Multifamily Development',
      description: '261-unit luxury apartment community with modern amenities in Grand Prairie, TX.',
      role: 'As Assistant Superintendent, I work with dozens of skilled subcontractors and vendors ensuring project quality and timely completion.'
    },
    {
      title: 'Responsive Web and Embedded Services',
      description: 'Custom web applications for construction management enhancements and automation.\nAs well as, embedded services for field management and minimal effort data collection.',
      role: 'Developing front-end interface, back-end pipeline and scalability, and implementation of user authentication.'
    },
  ];

  return (
    <PageContainer>
      <ContentWrapper>
        <FixedNavigation>
          <ScrollToTopLink to="/">HOME</ScrollToTopLink>
          <ScrollToTopLink to="/services">SERVICES</ScrollToTopLink>
          <ScrollToTopLink to="/education">EDUCATION</ScrollToTopLink>
          <ScrollToTopLink to="/contact">CONTACT</ScrollToTopLink>
        </FixedNavigation>
        
        <AboutMeSection>
          <motion.div variants={animations.fadeIn}>
            <PageHeading>About Me</PageHeading>
          </motion.div>
        </AboutMeSection>

        <BioSection>
          <BioBackground />
          <Container>
            <BioContent>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={animations.staggerContainer}
              >
                <motion.div variants={animations.fadeIn}>
                  <PageHeading>Ready to Get Started?</PageHeading>
                  <BioText>
                    Hi, I'm Matt Tap! A builder driven by a passion for solving complex problems with innovative solutions. 
                    I aim to spark a new era of construction, blending smarter practices with proven results, and redefining the scope of what we can achieve. 
                    While constantly pushing the boundaries, and building upon my expertise in both site work and software development, I hope to
                    give power back to the little guy. With a vision to build a business that transforms industries, I'm focused on creating solutions 
                    that weld optimized tools to the foundation of Industry Standard. My background in construction and 
                    computer science gives me a unique perspective on large scale architectures and how to approach complex projects with efficiency and creativity.
                  </BioText>
                </motion.div>
              </motion.div>
            </BioContent>
          </Container>
        </BioSection>

        <AboutMeSection>
          <Container>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={animations.staggerContainer}
            >
              <motion.div variants={animations.fadeIn}>
                <SectionHeading>Skills & Expertise</SectionHeading>
                <SkillsGrid>
                  {skills.map((skill, index) => (
                    <motion.div 
                      key={index} 
                      variants={animations.fadeIn}
                      whileHover={{ scale: 1.02 }}
                    >
                      <EnhancedSkillCard>
                        <SkillHeader>
                          <SkillTitle>{skill.name}</SkillTitle>
                          <SkillLevel>
                            <PercentageText>{skill.proficiency}%</PercentageText>
                            <ProgressBarContainer>
                              <ProgressBar width={skill.proficiency} />
                            </ProgressBarContainer>
                          </SkillLevel>
                        </SkillHeader>
                        <SkillDescription>{skill.description}</SkillDescription>
                      </EnhancedSkillCard>
                    </motion.div>
                  ))}
                </SkillsGrid>
              </motion.div>
            </motion.div>
          </Container>
        </AboutMeSection>

        <ProjectsSection>
          <Container>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={animations.staggerContainer}
            >
              <motion.div variants={animations.fadeIn}>
                <SectionHeading>Notable Projects</SectionHeading>
                <ProjectsGrid>
                  {projects.map((project, index) => (
                    <motion.div 
                      key={index} 
                      variants={animations.fadeIn}
                      whileHover={{ scale: 1.02 }}
                    >
                      <ProjectCard>
                        <ProjectTitle>{project.title}</ProjectTitle>
                        <ProjectDescription>{project.description}</ProjectDescription>
                        <ProjectRole>{project.role}</ProjectRole>
                      </ProjectCard>
                    </motion.div>
                  ))}
                </ProjectsGrid>
              </motion.div>
            </motion.div>
          </Container>
        </ProjectsSection>

        <BlogSection id="insights" className="insights-section">
          <Container>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={animations.staggerContainer}
            >
              <motion.div variants={animations.fadeIn}>
                <SectionHeading>Insights</SectionHeading>
                <BioText>
                  Explore my thoughts and opinions on topics ranging from construction and trending business models to optimizing and addressing LLM context window limitations. 
                  Every blue moon, a spark of curiosity will ignite in me thats not easily extinguished. I hope to use this feature as a medium to explore these ideas and share
                  them with those who are intrigued.
                  These short reads reflect my experiences and perspectives on industry trends and innovations.
                  I'm no expert and surely don't claim to be. I will do my best to ensure validity and accuracy in my posts,
                  but at the end of the day these are personal opinions and not an attempt at professional advice.
                  I hope to inspire you to learn something new or revisit an old idea with a fresh perspective.
                </BioText>
                <BlogGrid>
                  {blogPosts.map((post) => (
                    <motion.div key={post.id} variants={animations.fadeIn}>
                      <BlogCard 
                        as={Link} 
                        to={`/insights/${post.id}`}
                        style={{ textDecoration: 'none' }}
                      >
                        <BlogTitle>{post.title}</BlogTitle>
                        <TagsContainer>
                          {post.tags.map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                          ))}
                        </TagsContainer>
                        <BlogPreview>{post.preview}</BlogPreview>
                        <BlogMeta>
                          <BlogDate>{new Date(post.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}</BlogDate>
                          <BlogReadTime>{post.readTime} min read</BlogReadTime>
                        </BlogMeta>
                      </BlogCard>
                    </motion.div>
                  ))}
                </BlogGrid>
              </motion.div>
            </motion.div>
          </Container>
        </BlogSection>

        <BackToTopButton isVisible={showScrollButton} onClick={scrollToTop} />
      </ContentWrapper>
    </PageContainer>
  );
};

export default AboutMePage;